/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Table } from "semantic-ui-react";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../../slices/currentUser/currentUserSlice.js";
import { emptyReasonNotStarted } from "../../dictionaries/CodeSystemLookup.js";
import { globalLoadingOff, globalLoadingOn } from "../../slices/globalLoading/globalLoadingSlice.js";
import {
    checkIfStatisticAbsoluteOrRelative,
    codeMatchesPercentage,
    codeMatchesProportion,
    codeMatchesRelativeRisk,
    codeMatchesRiskDifference
} from "../compositionResourceFunctions/summaryOfFindingsFunctions.js";
import { clearDivWrapper, DisplayFromFHIRXhtml } from "../../datatypeComponents/NarrativeEntry.jsx";
import StringEntry from "../../datatypeComponents/StringEntry.jsx";
import ReferenceEntry from "../../datatypeComponents/ReferenceEntry.jsx";
import ChangeBaselineRiskEstimates from "./ChangeBaselineRiskEstimates.jsx";
import RateRelativeImportance from "./RateRelativeImportance.jsx";
import getNetEffectPointEstimate from "../compositionResourceFunctions/getNetEffectPointEstimate.js";
import getNetEffectEstimate from "../compositionResourceFunctions/getNetEffectEstimate.js";
import { selectEditMode } from "../../slices/editMode/editModeSlice.js";
import { resourceAdded, resourceUpdated, selectAllResources } from "../../slices/resourceCollection/resourceCollectionSlice.js";
import { fetchOutcomeImportance, selectAllImportance, selectOutcomeImportanceStatus } from "../../slices/outcomreImportance/outcomeImpotanceSlice.js";
import { fetchOutcomeVariables, outcomeVariableAdded, outcomeVariableUpdated, selectAllOutcomeVariables, selectOutcomeVariableById, selectOutcomeVariablesStatus } from "../../slices/outcomeVariables/outcomeVariablesSlice.js";
import { selectComparativeEvidenceMap } from "../../slices/comparativeEvidenceMap/comparativeEvidenceMapSlice.js";
import { selectColorScheme } from "../../slices/colorScheme/colorSchemeSlice.js";
import getIdFromReference from "../../functions/getIdFromReference.js";
import submitFhirJson from "../../functions/submitFhirJsonAPI.js";
import { baselineRiskAdded, baselineRiskUpdated, fetchBaselineRisk, selectAllBaselineRisks, selectBaselineRiskById, selectBaselineRiskStatus } from "../../slices/baselineRisk/baselineRiskSlice.js";
import addFoisFromFlisInTargetResources from "../compositionResourceFunctions/addFoisFromFlisInTargetResources.js";
import { loadNetEffectContributions, netEffectContributionAdded, netEffectContributionUpdated, selectAllNetEffectContributions, selectNetEffectContributionByTargetId, selectNetEffectContributionsStatus } from "../../slices/netEffectContributions/netEffectContributionsSlice.js";
import updateChangeReport from "../../slices/resourceChangeReport/updateChangeReport.js";
import { selectAllResourceChanges } from "../../slices/resourceChangeReport/resourceChangeReportSlice.js";
import { selectAllTopSections, selectTopSectionById, selectTopSectionsStatus, topSectionAdded, topSectionUpdated } from "../../slices/topSections/topSectionsSlice.js";
import ReassignOutcomes from "./ReassignOutcomes.jsx";

const extractControlEventRate = (baselineRiskEvidence, controlEventRate, controlEventRateDisplay) => {
    if (baselineRiskEvidence?.statistic?.length > 0) {
        for (const stat of baselineRiskEvidence.statistic) {
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0) {
                if (codeMatchesProportion(stat.statisticType.coding[0].code)) {
                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                        controlEventRate = stat.quantity.value * 100;
                        controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                        break;
                    }
                }
                if (codeMatchesPercentage(stat.statisticType.coding[0].code)) {
                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                        controlEventRate = stat.quantity.value;
                        controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                        break;
                    }
                }
                if (stat.statisticType.coding[0].code === 'STATO:0000047') {
                    if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value) && stat.sampleSize) {
                        let denominator = stat.sampleSize.numberAnalyzed || stat.sampleSize.knownDataCount || stat.sampleSize.numberOfParticipants || 0;
                        if (denominator > 0) {
                            controlEventRate = (stat.quantity.value / denominator) * 100;
                            controlEventRateDisplay = parseFloat(controlEventRate.toFixed(2)) + '%';
                            break;
                        }
                    }
                }
            }
        }
    }
    return [controlEventRate, controlEventRateDisplay];
}

const extractRiskDifferenceAndInterventionEventRate = (controlEventRate, riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
    riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
    riskDifferenceHighValueDisplay, comparativeEvidence, dispatch, compositionId) => {
    if (comparativeEvidence?.statistic?.length > 0) {
        let riskDifferenceStatistic;
        let riskRatioStatistic;
        let oddsRatioStatistic;
        for (const stat of comparativeEvidence.statistic) {
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 &&
                codeMatchesRiskDifference(stat.statisticType.coding[0].code)) {
                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                    riskDifferenceStatistic = stat;
                }
            }
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 &&
                (codeMatchesRelativeRisk(stat.statisticType.coding[0].code) || //risk ratio
                    stat.statisticType.coding[0].code === 'STATO:0000677' || stat.statisticType.coding[0].code === 'TBD:0000026' || //hazard ratio currently treated like risk ratio
                    stat.statisticType.coding[0].code === 'STATO:0000678' || stat.statisticType.coding[0].code === 'TBD:0000028' || //prevalence ratio currently treated like risk ratio
                    stat.statisticType.coding[0].code === 'STATO:0000680' || stat.statisticType.coding[0].code === 'TBD:0000027' //incidence rate ratio currently treated like risk ratio
                )) {
                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                    riskRatioStatistic = stat;
                }
            }
            if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 &&
                (stat.statisticType.coding[0].code === 'STATO:0000182' || stat.statisticType.coding[0].code === 'C16932')) {
                if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                    oddsRatioStatistic = stat;
                }
            }
        }
        if (oddsRatioStatistic) {
            let oddsRatio = oddsRatioStatistic.quantity.value;
            let controlOdds = controlEventRate / (100 - controlEventRate);
            let interventionOdds = controlOdds * oddsRatio;
            let interventionEventRate = (interventionOdds / (interventionOdds + 1)) * 100;
            interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
            riskDifference = interventionEventRate - controlEventRate;
            riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
            if (oddsRatioStatistic.attributeEstimate?.length > 0) {
                for (const attributeEstimate of oddsRatioStatistic.attributeEstimate) {
                    if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                        if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                            let lowORValue = Number(attributeEstimate.range.low.value);
                            let interventionOddsLow = controlOdds * lowORValue;
                            let interventionEventRateLow = (interventionOddsLow / (interventionOddsLow + 1)) * 100;
                            riskDifferenceLowValue = interventionEventRateLow - controlEventRate;
                            riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                        }
                        if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                            let highRRValue = Number(attributeEstimate.range.high.value);
                            let interventionOddsHigh = controlOdds * highRRValue;
                            let interventionEventRateHigh = (interventionOddsHigh / (interventionOddsHigh + 1)) * 100;
                            riskDifferenceHighValue = interventionEventRateHigh - controlEventRate;
                            riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                        }
                        let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                        riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                    }
                }
            }
        } else if (riskRatioStatistic) {
            let relativeRisk = riskRatioStatistic.quantity.value;
            let interventionEventRate = controlEventRate * relativeRisk;
            interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
            riskDifference = interventionEventRate - controlEventRate;
            riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
            if (riskRatioStatistic.attributeEstimate?.length > 0) {
                for (const attributeEstimate of riskRatioStatistic.attributeEstimate) {
                    if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                        if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                            let lowRRValue = Number(attributeEstimate.range.low.value);
                            riskDifferenceLowValue = (controlEventRate * lowRRValue) - controlEventRate;
                            riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                        }
                        if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                            let highRRValue = Number(attributeEstimate.range.high.value);
                            riskDifferenceHighValue = (controlEventRate * highRRValue) - controlEventRate;
                            riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                        }
                        let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                        riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                    }
                }
            }
        } else if (riskDifferenceStatistic) {
            riskDifference = riskDifferenceStatistic.quantity.value * 100;
            riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
            interventionEventRate = controlEventRate + riskDifference;
            interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
            if (riskDifferenceStatistic.attributeEstimate?.length > 0) {
                for (const attributeEstimate of riskDifferenceStatistic.attributeEstimate) {
                    if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                        if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                            riskDifferenceLowValue = Number(attributeEstimate.range.low.value) * 100;
                            riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                        }
                        if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                            riskDifferenceHighValue = Number(attributeEstimate.range.high.value) * 100;
                            riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                        }
                        let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                        riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                    }
                }
            }
            //create and add riskRatioStatistic with note of method of calculation
            let relativeRisk = interventionEventRate / controlEventRate;
            let lowRRValue = (riskDifferenceLowValue / controlEventRate) + 1;
            let highRRValue = (riskDifferenceHighValue / controlEventRate) + 1;
            let derivedRiskRatioStatistic = {
                "description": "risk ratio " + relativeRisk + " (" + lowRRValue + " to " + highRRValue + ") derived from risk difference and control event rate during the generation of the Summary of Net Effect",
                "statisticType": {
                    "coding": [
                        {
                            "system": "https://fevir.net/sevco",
                            "code": "STATO:0000245",
                            "display": "risk ratio"
                        }
                    ]
                },
                "quantity": {
                    "value": relativeRisk
                },
                "attributeEstimate": [
                    {
                        "type": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/sevco",
                                    "code": "STATO:0000196",
                                    "display": "confidence interval"
                                }
                            ]
                        },
                        "range": {
                            "low": { "value": lowRRValue },
                            "high": { "value": highRRValue }
                        }
                    }
                ]
            }
            let revisedComparativeEvidence = JSON.parse(JSON.stringify(comparativeEvidence));
            revisedComparativeEvidence.statistic.push(derivedRiskRatioStatistic);
            dispatch(resourceUpdated({
                id: comparativeEvidence.id,
                displayTitle: comparativeEvidence.title,
                resourceType: "Evidence",
                resourceJson: comparativeEvidence,
                revisedResourceJson: revisedComparativeEvidence,
                usageData: {
                    editpermission: true,
                    readpermission: true,
                    status: "active"
                }
            }));
        }
        if (isNaN(riskDifference)) {
            if (!isNaN(controlEventRate)) {
                for (const stat of comparativeEvidence.statistic) {
                    if (stat.statisticType && stat.statisticType.coding && stat.statisticType.coding.length > 0 && stat.statisticType.coding[0].code) {
                        const statCode = stat.statisticType.coding[0].code;
                        if (checkIfStatisticAbsoluteOrRelative(statCode) === "absolute") {
                            if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                let absoluteDifference = stat.quantity.value * 100;
                                interventionEventRate = controlEventRate + absoluteDifference;
                                interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                                riskDifference = absoluteDifference;
                                riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                                if (stat.attributeEstimate?.length > 0) {
                                    for (const attributeEstimate of stat.attributeEstimate) {
                                        if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                            if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                                riskDifferenceLowValue = Number(attributeEstimate.range.low.value) * 100;
                                                riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                            }
                                            if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                                riskDifferenceHighValue = Number(attributeEstimate.range.high.value) * 100;
                                                riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                            }
                                            let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                            riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                        }
                                    }
                                }
                            }
                        } else if (checkIfStatisticAbsoluteOrRelative(statCode) === "relative") {
                            if (stat.quantity && stat.quantity.value !== undefined && !isNaN(stat.quantity.value)) {
                                let relativeDifference = stat.quantity.value;
                                interventionEventRate = controlEventRate * relativeDifference;
                                interventionEventRateDisplay = parseFloat(interventionEventRate.toFixed(2)) + '%';
                                riskDifference = interventionEventRate - controlEventRate;
                                riskDifferenceDisplay = parseFloat(riskDifference.toFixed(2)) + '%';
                                if (stat.attributeEstimate?.length > 0) {
                                    for (const attributeEstimate of stat.attributeEstimate) {
                                        if (attributeEstimate.range?.low?.value || attributeEstimate.range?.high?.value) {
                                            if (attributeEstimate.range.low && attributeEstimate.range.low.value !== undefined && !isNaN(attributeEstimate.range.low.value)) {
                                                let lowRRValue = Number(attributeEstimate.range.low.value);
                                                riskDifferenceLowValue = (controlEventRate * lowRRValue) - controlEventRate;
                                                riskDifferenceLowValueDisplay = parseFloat((riskDifferenceLowValue).toFixed(2)).toString() + '%';
                                            }
                                            if (attributeEstimate.range.high && attributeEstimate.range.high.value !== undefined && !isNaN(attributeEstimate.range.high.value)) {
                                                let highRRValue = Number(attributeEstimate.range.high.value);
                                                riskDifferenceHighValue = (controlEventRate * highRRValue) - controlEventRate;
                                                riskDifferenceHighValueDisplay = parseFloat((riskDifferenceHighValue).toFixed(2)).toString() + '%';
                                            }
                                            let riskDifferenceAttributeEstimateDisplay = ' (' + riskDifferenceLowValueDisplay + ' to ' + riskDifferenceHighValueDisplay + ')';
                                            riskDifferenceDisplay += riskDifferenceAttributeEstimateDisplay;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    return [riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
        riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
        riskDifferenceHighValueDisplay];
}

const calculateNetEffectContributionWithConfidence = (riskDifference, relativeImportance, desirability, riskDifferenceLowValue, riskDifferenceHighValue) => {
    let netEffectContribution = NaN;
    let netEffectContributionLow = NaN;
    let netEffectContributionHigh = NaN;

    try {
        if (desirability === -1 || desirability === "-1" || desirability.toLowerCase() === 'undesirable') {
            desirability = -1;
        } else {
            desirability = 1;
        }
    } catch { desirability = 1; }
    netEffectContribution = riskDifference * relativeImportance * desirability;
    netEffectContribution = parseFloat(netEffectContribution.toFixed(2));
    netEffectContributionLow = riskDifferenceLowValue * relativeImportance * desirability;
    netEffectContributionLow = parseFloat(netEffectContributionLow.toFixed(2));
    netEffectContributionHigh = riskDifferenceHighValue * relativeImportance * desirability;
    netEffectContributionHigh = parseFloat(netEffectContributionHigh.toFixed(2));

    if (netEffectContributionLow < netEffectContributionHigh) {
        return [netEffectContribution, netEffectContributionLow, netEffectContributionHigh];
    } else {
        return [netEffectContribution, netEffectContributionHigh, netEffectContributionLow];
    }
};

const createNetEffectContributionDisplay = (netEffectContribution, netEffectContributionLow, netEffectContributionHigh) => {
    let netEffectContributionLowDisplay = "Not calculable";
    let netEffectContributionHighDisplay = "Not calculable";
    if (!isNaN(netEffectContribution)) {
        netEffectContribution = netEffectContribution / 100;
        if (!isNaN(netEffectContributionLow)) {
            netEffectContributionLow = netEffectContributionLow / 100;
            netEffectContributionLowDisplay = valueSpanWithStyle(netEffectContributionLow, 2, false);
        }
        if (!isNaN(netEffectContributionHigh)) {
            netEffectContributionHigh = netEffectContributionHigh / 100;
            netEffectContributionHighDisplay = valueSpanWithStyle(netEffectContributionHigh, 2, false);
        }
    }

    let netEffectContributionDisplay = valueSpanWithStyle(netEffectContribution, 2, true);
    if (netEffectContributionDisplay === "NaN") {
        netEffectContributionDisplay = "[Not calculable.]"
    }
    if (!isNaN(netEffectContributionLow) || !isNaN(netEffectContributionHigh)) {
        netEffectContributionDisplay += " (" + netEffectContributionLowDisplay + " to " + netEffectContributionHighDisplay + ")";
    }
    return netEffectContributionDisplay;
}

const valueSpanWithStyle = (value, decimalPoints = 2, bold = false) => {
    if (isNaN(value)) {
        return "[Not calculable.]"; //parseFloat(value).toString(); //value
    }
    let openSpan = "<span>";
    if (value !== 0) {
        let openColor = value > 0 ? "#00AA00" : "#AA0000";
        openSpan = '<span style="color: ' + openColor + '">';
    }
    if (bold) {
        return openSpan + "<b>" + parseFloat(value.toFixed(decimalPoints)).toString() + "</b></span>";
    } else {
        return openSpan + parseFloat(value.toFixed(decimalPoints)).toString() + "</span>";
    }
};

const submitNewRowChange = (addTableRowModalState, newRowSectionsState,
    dispatch, setOpenAddTableRowModalState) => {
    let tableRowSectionCode = {
        "coding": [
            {
                "system": "https://fevir.net/resources/CodeSystem/179423",
                "code": "summary-of-net-effect-contributions-entry-for-a-single-outcome",
                "display": "Summary of net effect contributions entry for a single outcome"
            }
        ],
        "text": "Summary of net effect contributions entry for a single outcome"
    };
    if (addTableRowModalState.newRowTitle) {
        let newRow = {
            "title": addTableRowModalState.newRowTitle,
            "code": tableRowSectionCode,
            "focus": addTableRowModalState.newRowFocus || { "display": "[No data]" },
            "section": newRowSectionsState.newRowSections
        };
        newRow.id = getIdFromReference(newRow.focus);
        newRow.itemChanged = true;
        dispatch(netEffectContributionAdded(newRow));
        setOpenAddTableRowModalState(prevState => { return { ...prevState, openModal: false }; });
    }
}

const generateNewTableRow = async (sourceReferencesState, setNewRowSectionsState, ratingDictionary,
    compositionId, userId, userName, combinedDictionary, apiToken, dispatch) => {
    let newRowSections = [
        {
            "title": "Outcome",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "outcome-measure",
                        "display": "Outcome Measure"
                    }
                ]
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        },
        {
            "title": "Desirability",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "outcome-desirability",
                        "display": "Outcome Desirability"
                    }
                ],
                "text": "Outcome Desirability"
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        },
        {
            "title": "Relative Importance",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "relative-importance",
                        "display": "Relative Importance"
                    }
                ],
                "text": "Relative Importance"
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        },
        {
            "title": "Result Without Treatment",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "result-with-comparator-alone",
                        "display": "Result with comparator alone"
                    }
                ]
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        },
        {
            "title": "Result With Treatment--Derived",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "result-with-intervention-alone-calculated",
                        "display": "Result with intervention alone (calculated)"
                    }
                ],
                "text": "result with intervention with calculated value derived from the control group and effect estimate"
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        },
        {
            "title": "Effect Estimate",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "result-with-intervention-vs-comparator",
                        "display": "Result with intervention vs. comparator"
                    }
                ]
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        },
        {
            "title": "Net Effect Contribution",
            "code": {
                "coding": [
                    {
                        "system": "https://fevir.net/resources/CodeSystem/179423",
                        "code": "net-effect-contribution",
                        "display": "Net Effect Contribution"
                    }
                ],
                "text": "Net Effect Contribution"
            },
            "text": {
                "status": "empty",
                "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
            },
            "entry": [],
            "emptyReason": emptyReasonNotStarted
        }
    ];
    let targetFoi;
    if (sourceReferencesState.outcomeDefinition) {
        newRowSections[0].entry.push(sourceReferencesState.outcomeDefinition);
        newRowSections[0].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + sourceReferencesState.outcomeDefinition.display + "</p></div>"
        };
        targetFoi = getIdFromReference(sourceReferencesState.outcomeDefinition);
        delete newRowSections[0].emptyReason;
    }
    let relativeImportanceMultiplier = 0;
    let desirability = "undesirable";
    if (sourceReferencesState.outcomeImportance) {
        newRowSections[1].entry.push(sourceReferencesState.outcomeImportance);
        delete newRowSections[1].emptyReason;
        newRowSections[2].entry.push(sourceReferencesState.outcomeImportance);
        delete newRowSections[2].emptyReason;
        if (targetFoi && ratingDictionary[targetFoi]?.percentageValue) {
            newRowSections[1].text = {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (ratingDictionary[targetFoi].desirabilityDisplay || "Presumed Undesirable") + "</p></div>"
            };
            newRowSections[2].text = {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + ratingDictionary[targetFoi].percentageValue + "%</p></div>"
            };
            if (ratingDictionary[targetFoi].desirabilityDisplay === "Desirable") {
                desirability = 1;
            }
            relativeImportanceMultiplier = ratingDictionary[targetFoi].percentageValue;
        }
        if (!relativeImportanceMultiplier) {
            let outcomeRatingFoi = getIdFromReference(sourceReferencesState.outcomeImportance);
            let outcomeImportance = combinedDictionary[outcomeRatingFoi];
            if (outcomeImportance?.content?.length > 0) {
                for (const content of outcomeImportance.content) {
                    if (content.type?.coding?.[0]?.code === "outcome-desirability") {
                        newRowSections[1].text = {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (content.classifier?.[0]?.coding?.[0]?.display || "Presumed Undesirable") + "</p></div>"
                        };
                        if (content.classifier?.[0]?.coding?.[0]?.display === "Desirable") {
                            desirability = 1;
                        }
                    } else if (content.type?.coding?.[0]?.code === "relative-importance") {
                        newRowSections[2].text = {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + content.quantity?.value + "%</p></div>"
                        };
                        if (content.quantity?.value) {
                            relativeImportanceMultiplier = content.quantity.value;
                        }
                    }
                }
            }
        }
    }
    let controlEventRate = NaN;
    let controlEventRateDisplay = "[Not a proportion.]";
    if (sourceReferencesState.comparatorOnlyEvidence) {
        newRowSections[3].entry.push(sourceReferencesState.comparatorOnlyEvidence);
        delete newRowSections[3].emptyReason;
        newRowSections[4].entry.push(sourceReferencesState.comparatorOnlyEvidence);
        newRowSections[4].entry.push(sourceReferencesState.comparativeEvidence);
        delete newRowSections[4].emptyReason;
        let baselineRiskFoi = getIdFromReference(sourceReferencesState.comparatorOnlyEvidence);
        let baselineRiskEvidence = combinedDictionary[baselineRiskFoi];
        [controlEventRate, controlEventRateDisplay] = extractControlEventRate(baselineRiskEvidence, controlEventRate, controlEventRateDisplay);
        newRowSections[3].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + controlEventRateDisplay + "</p></div>"
        };
    }
    let riskDifference = NaN;
    let riskDifferenceDisplay = '[Not a difference in proportions.]';
    let interventionEventRate = NaN;
    let interventionEventRateDisplay = '[Not a proportion.]';
    let riskDifferenceLowValue = NaN;
    let riskDifferenceLowValueDisplay = 'No lower bound';
    let riskDifferenceHighValue = NaN;
    let riskDifferenceHighValueDisplay = 'No upper bound';
    let comparativeEvidenceTitle;
    let comparativeEvidence;
    if (sourceReferencesState.comparativeEvidence) {
        newRowSections[5].entry.push(sourceReferencesState.comparativeEvidence);
        delete newRowSections[5].emptyReason;
        let effectEstimateFoi = getIdFromReference(sourceReferencesState.comparativeEvidence);
        comparativeEvidence = combinedDictionary[effectEstimateFoi];
        comparativeEvidenceTitle = comparativeEvidence?.title;

        [riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
            riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
            riskDifferenceHighValueDisplay] = extractRiskDifferenceAndInterventionEventRate(controlEventRate, riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
                riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
                riskDifferenceHighValueDisplay, comparativeEvidence, dispatch, compositionId);

        newRowSections[4].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + interventionEventRateDisplay + "</p></div>"
        };
        newRowSections[5].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + riskDifferenceDisplay + "</p></div>"
        };
    }
    let [netEffectContribution, netEffectContributionLow,
        netEffectContributionHigh] = calculateNetEffectContributionWithConfidence(riskDifference,
            relativeImportanceMultiplier, desirability, riskDifferenceLowValue, riskDifferenceHighValue);
    let netEffectContributionDisplay = createNetEffectContributionDisplay(netEffectContribution, netEffectContributionLow, netEffectContributionHigh);

    newRowSections[6].text = {
        "status": "generated",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
    };
    if (netEffectContributionDisplay !== "[Not calculable.]") {
        //create the NetEffectContribution Evidence Resource
        let netEffectContributionEvidence = {
            "resourceType": "Evidence",
            "id": "",
            "meta": {
                "profile": [
                    "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-contribution"
                ]
            },
            "contained": [comparativeEvidence.contained],
            "title": "NetEffectContribution for " + comparativeEvidenceTitle + " for " + userName,
            "status": "active",
            "author": [
                {
                    "name": "Computable Publishing: Net Effect Generator"
                },
                {
                    "name": userName
                }
            ],
            "publisher": "Computable Publishing LLC",
            "contact": [
                {
                    "telecom": [
                        {
                            "system": "email",
                            "value": "support@computablepublishing.com"
                        }
                    ]
                }
            ],
            "copyright": "https://creativecommons.org/licenses/by-nc-sa/4.0/",
            "variableDefinition": [
                comparativeEvidence.variableDefinition?.[0] ||
                {
                    "description": "population definition missing in comparative evidence",
                    "variableRole": "population"
                },
                comparativeEvidence.variableDefinition?.[1] ||
                {
                    "description": "exposure definition missing in comparative evidence",
                    "variableRole": "exposure"
                },
                comparativeEvidence.variableDefinition?.[2] ||
                {
                    "description": "outcome definition missing in comparative evidence",
                    "variableRole": "outcome"
                },
                {
                    "extension": [
                        {
                            "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/relative-outcome-importance",
                            "valueReference": sourceReferencesState.outcomeImportance
                        }
                    ],
                    "variableRole": "covariate",
                    "roleSubtype": {
                        "text": "Relative Value Multiplier"
                    },
                    "observed": {
                        "display": "Relative Importance Multiplier"
                    }
                }
            ],
            "statistic": [
                {
                    "statisticType": {
                        "coding": [
                            {
                                "system": "https://fevir.net/sevco",
                                "code": "STATO:0000424",
                                "display": "risk difference"
                            }
                        ]
                    },
                    "quantity": {
                        "value": netEffectContribution
                    },
                    "attributeEstimate": [
                        {
                            "type": {
                                "coding": [
                                    {
                                        "system": "https://fevir.net/sevco",
                                        "code": "STATO:0000196",
                                        "display": "confidence interval"
                                    }
                                ]
                            },
                            "level": 0.95,
                            "range": {
                                "low": {
                                    "value": netEffectContributionLow
                                },
                                "high": {
                                    "value": netEffectContributionHigh
                                }
                            }
                        }
                    ],
                    "modelCharacteristic": [
                        {
                            "code": {
                                "text": "Net effect contribution analysis"
                            }
                        }
                    ]
                }
            ]
        };
        if (!comparativeEvidence.contained) {
            delete netEffectContributionEvidence.contained;
        }
        let resourceFOI;
        let response = await submitFhirJson(netEffectContributionEvidence, netEffectContributionEvidence.title, apiToken);
        if (response?.success) {
            resourceFOI = response.formstateid;
            netEffectContributionEvidence.id = resourceFOI;
            let resourceBody = {
                id: resourceFOI,
                displayTitle: netEffectContributionEvidence.title,
                resourceType: "Evidence",
                resourceJson: netEffectContributionEvidence,
                revisedResourceJson: netEffectContributionEvidence,
                usageData: {
                    totalviews: 0,
                    totaledits: 1,
                    totalusersviewed: 0,
                    totalusersedited: 1,
                    editpermission: true,
                    readpermission: true,
                    adminpermission: true,
                    editors: [{ userid: userId, name: userName }],
                    status: "active"
                }
            }
            dispatch(resourceAdded(resourceBody));
        }
    }
    setNewRowSectionsState({
        "newRowSections": newRowSections,
        "tableRowContentGenerated": true
    });
}

const AddTableRowModal = ({ setOpenAddTableRowModalState, setTriggerReloadState }) => {

    const dispatch = useDispatch();
    const outcomeVariables = useSelector((state) => selectAllOutcomeVariables(state));
    const outcomeDictionary = {};
    for (let key in outcomeVariables) {
        outcomeDictionary[outcomeVariables[key].id] = outcomeVariables[key];
    }
    const comparativeEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const combinedDictionary = comparativeEvidenceMap.combinedDictionary;
    const compositionId = comparativeEvidenceMap.compositionId;
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const apiToken = useSelector(selectCurrentUserApiToken);

    const ratingResources = useSelector(state => selectAllImportance(state));
    const ratingDictionary = {};
    for (let key in ratingResources) {
        ratingDictionary[ratingResources[key].id] = ratingResources[key];
    }

    const [addTableRowModalState, setAddTableRowModalState] = useState({
        "newRowTitle": "",
        "newRowFocus": "",
        "newRowSections": []
    });
    const [newRowSectionsState, setNewRowSectionsState] = useState({});
    const [sourceReferencesState, setSourceReferencesState] = useState({
        outcomeDefinition: "", comparatorOnlyEvidence: "",
        comparativeEvidence: "", outcomeImportance: ""
    });

    useEffect(() => {
        if (newRowSectionsState.tableRowContentGenerated) {
            submitNewRowChange(addTableRowModalState, newRowSectionsState,
                dispatch, setOpenAddTableRowModalState);
            console.log("setTriggerReloadState to true due to change in newRowSectionState")
            setTriggerReloadState(true);
        }
    }, [newRowSectionsState]);

    useEffect((() => {
        if (addTableRowModalState.newRowTitle) {
            setSourceReferencesState(prevState => {
                return {
                    ...prevState,
                    "outcomeDefinition": {
                        ...prevState.outcomeDefinition,
                        "display": addTableRowModalState.newRowTitle
                    }
                }
            });
        }
    }), [addTableRowModalState.newRowTitle]);

    useEffect(() => {
        if (sourceReferencesState.outcomeDefinition) {
            let newRowFocus = JSON.parse(JSON.stringify(sourceReferencesState.outcomeDefinition))
            setAddTableRowModalState(prevState => { return { ...prevState, newRowFocus: newRowFocus } });
            let outcomeId = getIdFromReference(newRowFocus);
            if (outcomeId && !isNaN(outcomeId)) {
                if (outcomeDictionary[outcomeId]) {
                    let outcomeBody = {
                        id: outcomeId,
                        title: outcomeDictionary[outcomeId].title || outcomeDictionary[outcomeId].name,
                        titletrimmed: outcomeDictionary[outcomeId].title || outcomeDictionary[outcomeId].name,
                        fli: "",
                        resource: outcomeDictionary[outcomeId],
                        reference: newRowFocus
                    }
                    dispatch(outcomeVariableUpdated(outcomeBody));
                } else {
                    let outcomeResource = combinedDictionary[outcomeId];
                    if (outcomeResource) {
                        let outcomeBody = {
                            id: outcomeId,
                            title: outcomeResource.title || outcomeResource.name,
                            titletrimmed: (outcomeResource.title || outcomeResource.name).replace("OutcomeDefinition: ", "").trim(),
                            fli: "",
                            resource: outcomeResource,
                            reference: newRowFocus
                        }
                        dispatch(outcomeVariableAdded(outcomeBody));
                    }
                }
            }
        }
    }, [sourceReferencesState.outcomeDefinition]);

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <Button style={{ padding: "6px", position: "absolute", right: "14px" }}
            className="formButton negative"
            content="✖"
            onClick={() => {
                setOpenAddTableRowModalState(prevState => { return { ...prevState, openModal: false }; });
            }} />
        <h3>Name the Outcome (how it will show in the table row header)</h3>
        <br />
        <StringEntry elementName='newRowTitle' fieldLabel='Title' required
            startingValue={addTableRowModalState.newRowTitle}
            setResourceState={setAddTableRowModalState} />
        <br />
        <h3>Set or create the structured Outcome Definition</h3>
        <br />
        <ReferenceEntry elementName='outcomeDefinition' enableCreation={true}
            fieldLabel='OutcomeDefinition (Resource Reference)'
            referencedResourceTypes={['EvidenceVariable']}
            addElementValues={{ title: addTableRowModalState.newRowTitle }}
            startingResourceType="EvidenceVariable" setProfile="VariableDefinition"
            startingValue={sourceReferencesState.outcomeDefinition}
            setResourceState={setSourceReferencesState} />
        <br />
        <h3>Set or create the structured Comparator Only Evidence</h3>
        <br />
        <ReferenceEntry elementName='comparatorOnlyEvidence' enableCreation={true}
            fieldLabel='ComparatorOnlyEvidence (Resource Reference)'
            referencedResourceTypes={['Evidence']}
            startingResourceType="Evidence" setProfile="ComparatorOnlyEvidence"
            addElementValues={{
                title: "Control Evidence (Baseline Risk) for " + sourceReferencesState.outcomeDefinition?.display,
                variableDefinition: [
                    {
                        "description": "comparator only group",
                        "variableRole": "population"
                    },
                    {
                        "description": sourceReferencesState.outcomeDefinition?.display,
                        "variableRole": "outcome",
                        "observed": sourceReferencesState.outcomeDefinition
                    }
                ]
            }}
            startingValue={sourceReferencesState.comparatorOnlyEvidence}
            setResourceState={setSourceReferencesState} />
        <br />
        <h3>Set or create the structured Comparative Evidence</h3>
        <br />
        <ReferenceEntry elementName='comparativeEvidence' enableCreation={true}
            fieldLabel='ComparativeEvidence (Resource Reference)'
            referencedResourceTypes={['Evidence']}
            startingResourceType="Evidence" setProfile="ComparativeEvidence"
            addElementValues={{
                title: "Comparative Evidence for " + sourceReferencesState.outcomeDefinition?.display,
                variableDefinition: [
                    {
                        "description": "total group",
                        "variableRole": "population"
                    },
                    {
                        "description": "intervention group vs. comparator group",
                        "variableRole": "exposure"
                    },
                    {
                        "description": sourceReferencesState.outcomeDefinition?.display,
                        "variableRole": "outcome",
                        "observed": sourceReferencesState.outcomeDefinition
                    }
                ]
            }}
            startingValue={sourceReferencesState.comparativeEvidence}
            setResourceState={setSourceReferencesState} />
        <br />
        <h3>Set or create the structured Outcome Importance (Rating)</h3>
        <br />
        <ReferenceEntry elementName='outcomeImportance' enableCreation={true}
            fieldLabel='Outcome Importance Rating (Resource Reference)'
            referencedResourceTypes={['ArtifactAssessment']}
            startingResourceType="ArtifactAssessment" setProfile="OutcomeImportance"
            addElementValues={{
                title: "Importance of " + sourceReferencesState.outcomeDefinition?.display + " for " + userName,
                artifactReference: sourceReferencesState.outcomeDefinition
            }}
            startingValue={sourceReferencesState.outcomeImportance}
            setResourceState={setSourceReferencesState} />
        <br />
        <br />
        {sourceReferencesState.comparativeEvidence?.reference &&
            <span>
                <Button style={{ color: "#FFFFFF", width: "230px", float: "left" }} className="formButton"
                    content="Generate Table Row" positive
                    onClick={() => {
                        generateNewTableRow(sourceReferencesState, setNewRowSectionsState, ratingDictionary,
                            compositionId, userId, userName, combinedDictionary, apiToken, dispatch);
                    }}
                    disabled={!addTableRowModalState.newRowTitle}
                />
                &nbsp;&nbsp;
                {!addTableRowModalState.newRowTitle && <>Enter a title at the top to submit changes.</>}
            </span>}
        <br />
    </div>;

    return (
        <Modal
            style={{ padding: "0px", margin: "0px", height: "96%" }}
            dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
            open={true}
            centered={false}
            content={modalContent}
        />
    )
};

const loadParameterResources = async (dispatch, netEffectContributions,
    apiToken, combinedDictionary, resourceCollection, subject) => {
    dispatch(globalLoadingOn());
    let resourceDictionary = JSON.parse(JSON.stringify(combinedDictionary));
    for (const entry of resourceCollection) {
        resourceDictionary[entry.id] = entry.revisedResourceJson;
    }
    let targetResources = [];
    let fliListToProcess = [];
    targetResources = netEffectContributions.map(section => {
        let targetfoi = section.focus?.reference?.replace("EvidenceVariable/", "");
        let targetfli;
        if (!targetfoi && section.focus?.identifier?.system === "https://fevir.net/FLI" && section.focus.identifier.value) {
            targetfli = section.focus.identifier.value;
        }
        let targettitle = section.focus?.display;
        if (!targetfoi && targetfli) {
            fliListToProcess.push(targetfli);
        }
        return {
            id: targetfoi,
            title: targettitle,
            fli: targetfli
        };
    });
    if (fliListToProcess.length > 0) {
        await addFoisFromFlisInTargetResources(targetResources, apiToken);
    }
    if (targetResources.length > 0) {
        let ratingBody = {
            targetResources: targetResources,
            customized: false,
            subject: subject
        };
        dispatch(fetchOutcomeImportance(ratingBody));
        let body = {
            findingsSection: netEffectContributions,
            combinedDictionary: resourceDictionary,
            resourceCollection: resourceCollection
        };
        dispatch(fetchOutcomeVariables(body));
        dispatch(fetchBaselineRisk(body));
    }
    dispatch(globalLoadingOff());
};

//mutates findingsSection and returns netEffectEstimateSection
const recalculateFindingsSectionFromReferences = async (findingsSection, ratingDictionary, combinedDictionary, dispatch,
    userId, userName, compositionId, apiToken, priorNetEffectEstimateSection, baselineRiskResources, resourceChanges) => {
    console.log("recalculateFindingsSectionFromReferences")
    const baselineRiskDictionary = {};
    for (let item of baselineRiskResources) {
        baselineRiskDictionary[item.id] = item;
    }
    let netEffectPointEstimate = 0;
    let netEffectStandardDeviationSquared = 0;
    for (let section of findingsSection) {
        if (section.section && section.focus?.reference?.replace("EvidenceVariable/", "")) {
            let targetFoi = section.focus.reference.replace("EvidenceVariable/", "");
            section.section[0].text = {
                "status": "generated",
                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (section.focus?.display || section.title) + "</p></div>"
            };
            let relativeImportanceMultiplier = 0;
            let desirability = "undesirable";
            let importanceReference;
            if (ratingDictionary[targetFoi]?.percentageValue) {
                section.section[1].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (ratingDictionary[targetFoi].desirabilityDisplay || "Presumed Undesirable") + "</p></div>"
                };
                if (ratingDictionary[targetFoi].importanceReference) {
                    section.section[1].entry = [ratingDictionary[targetFoi].importanceReference];
                    importanceReference = ratingDictionary[targetFoi].importanceReference;
                }
                if (ratingDictionary[targetFoi].desirabilityDisplay === "Desirable") {
                    desirability = 1;
                }
                section.section[2].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + ratingDictionary[targetFoi].percentageValue + "%</p></div>"
                };
                if (ratingDictionary[targetFoi].importanceReference) {
                    section.section[2].entry = [ratingDictionary[targetFoi].importanceReference];
                }
                if (ratingDictionary[targetFoi].percentageValue) {
                    relativeImportanceMultiplier = ratingDictionary[targetFoi].percentageValue;
                }
            }
            if (!relativeImportanceMultiplier && section.section[2]?.entry?.[0]) {
                importanceReference = section.section[2].entry[0];
                let outcomeRatingFoi = getIdFromReference(section.section[2].entry[0]);
                let outcomeImportance = combinedDictionary[outcomeRatingFoi];
                if (outcomeImportance?.content?.length > 0) {
                    for (const content of outcomeImportance.content) {
                        if (content.type?.coding?.[0]?.code === "outcome-desirability") {
                            section.section[1].text = {
                                "status": "generated",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (content.classifier?.[0]?.coding?.[0]?.display || "Presumed Undesirable") + "</p></div>"
                            };
                            if (content.classifier?.[0]?.coding?.[0]?.display === "Desirable") {
                                desirability = 1;
                            }
                        } else if (content.type?.coding?.[0]?.code === "relative-importance") {

                            section.section[2].text = {
                                "status": "generated",
                                "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + content.quantity?.value + "%</p></div>"
                            };
                            if (content.quantity?.value) {
                                relativeImportanceMultiplier = content.quantity.value;
                            }
                        }
                    }
                }
            }
            let controlEventRate = NaN;
            let controlEventRateDisplay = "[Not a proportion.]";
            if (baselineRiskDictionary[targetFoi]?.resource) {
                [controlEventRate, controlEventRateDisplay] = extractControlEventRate(baselineRiskDictionary[targetFoi].resource, controlEventRate, controlEventRateDisplay);
                section.section[3].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + controlEventRateDisplay + "</p></div>"
                };
                section.section[3].entry = [baselineRiskDictionary[targetFoi].reference];
            }
            if (isNaN(controlEventRate) && section.section[3]?.entry?.[0]) {
                let baselineRiskFoi = getIdFromReference(section.section[3].entry[0]);
                let baselineRiskEvidence = combinedDictionary[baselineRiskFoi];
                [controlEventRate, controlEventRateDisplay] = extractControlEventRate(baselineRiskEvidence, controlEventRate, controlEventRateDisplay);
                section.section[3].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + controlEventRateDisplay + "</p></div>"
                };
            }
            let riskDifference = NaN;
            let riskDifferenceDisplay = '[Not a difference in proportions.]';
            let interventionEventRate = NaN;
            let interventionEventRateDisplay = '[Not a proportion.]';
            let riskDifferenceLowValue = NaN;
            let riskDifferenceLowValueDisplay = 'No lower bound';
            let riskDifferenceHighValue = NaN;
            let riskDifferenceHighValueDisplay = 'No upper bound';
            let comparativeEvidenceTitle;
            let comparativeEvidence;
            if (section.section[5]?.entry?.[0]) {
                let effectEstimateFoi = getIdFromReference(section.section[5].entry[0]);
                comparativeEvidence = combinedDictionary[effectEstimateFoi];
                comparativeEvidenceTitle = comparativeEvidence?.title;

                [riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
                    riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
                    riskDifferenceHighValueDisplay] = extractRiskDifferenceAndInterventionEventRate(controlEventRate, riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
                        riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
                        riskDifferenceHighValueDisplay, comparativeEvidence, dispatch, compositionId);

                section.section[4].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + interventionEventRateDisplay + "</p></div>"
                };
                section.section[5].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + riskDifferenceDisplay + "</p></div>"
                };
            }
            let [netEffectContribution, netEffectContributionLow,
                netEffectContributionHigh] = calculateNetEffectContributionWithConfidence(riskDifference,
                    relativeImportanceMultiplier, desirability, riskDifferenceLowValue, riskDifferenceHighValue);
            let netEffectContributionDisplay = createNetEffectContributionDisplay(netEffectContribution, netEffectContributionLow, netEffectContributionHigh);
            if (!isNaN(netEffectContribution)) {
                netEffectPointEstimate += netEffectContribution / 100;
            }
            if (!section.section[6].text ||
                section.section[6].text.div !== "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
            ) {
                section.section[6].text = {
                    "status": "generated",
                    "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
                };
                if (netEffectContributionDisplay !== "[Not calculable.]") {
                    //create or replace the NetEffectContribution Evidence Resource
                    let netEffectContributionEvidence = {
                        "resourceType": "Evidence",
                        "id": "",
                        "meta": {
                            "profile": [
                                "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-contribution"
                            ]
                        },
                        "contained": [comparativeEvidence.contained],
                        "title": "NetEffectContribution for " + comparativeEvidenceTitle,
                        "status": "active",
                        "author": [
                            {
                                "name": "Computable Publishing: Net Effect Generator"
                            },
                            {
                                "name": userName
                            }
                        ],
                        "publisher": "Computable Publishing LLC",
                        "contact": [
                            {
                                "telecom": [
                                    {
                                        "system": "email",
                                        "value": "support@computablepublishing.com"
                                    }
                                ]
                            }
                        ],
                        "copyright": "https://creativecommons.org/licenses/by-nc-sa/4.0/",
                        "variableDefinition": [
                            comparativeEvidence.variableDefinition?.[0] ||
                            {
                                "description": "population definition missing in comparative evidence",
                                "variableRole": "population"
                            },
                            comparativeEvidence.variableDefinition?.[1] ||
                            {
                                "description": "exposure definition missing in comparative evidence",
                                "variableRole": "exposure"
                            },
                            comparativeEvidence.variableDefinition?.[2] ||
                            {
                                "description": "outcome definition missing in comparative evidence",
                                "variableRole": "outcome"
                            },
                            {
                                "extension": [
                                    {
                                        "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/relative-outcome-importance",
                                        "valueReference": importanceReference
                                    }
                                ],
                                "variableRole": "covariate",
                                "roleSubtype": {
                                    "text": "Relative Value Multiplier"
                                },
                                "observed": {
                                    "display": "Relative Importance Multiplier"
                                }
                            }
                        ],
                        "statistic": [
                            {
                                "statisticType": {
                                    "coding": [
                                        {
                                            "system": "https://fevir.net/sevco",
                                            "code": "STATO:0000424",
                                            "display": "risk difference"
                                        }
                                    ]
                                },
                                "quantity": {
                                    "value": netEffectContribution
                                },
                                "attributeEstimate": [
                                    {
                                        "type": {
                                            "coding": [
                                                {
                                                    "system": "https://fevir.net/sevco",
                                                    "code": "STATO:0000196",
                                                    "display": "confidence interval"
                                                }
                                            ]
                                        },
                                        "level": 0.95,
                                        "range": {
                                            "low": {
                                                "value": netEffectContributionLow
                                            },
                                            "high": {
                                                "value": netEffectContributionHigh
                                            }
                                        }
                                    }
                                ],
                                "modelCharacteristic": [
                                    {
                                        "code": {
                                            "text": "Net effect contribution analysis"
                                        }
                                    }
                                ]
                            }
                        ]
                    };
                    if (!comparativeEvidence.contained) {
                        delete netEffectContributionEvidence.contained;
                    }
                    let netEffectContributionFoi;
                    if (section.section[6].entry?.[0]?.reference) {
                        netEffectContributionFoi = getIdFromReference(section.section[6].entry[0]);
                    }
                    if (combinedDictionary[netEffectContributionFoi]) {
                        netEffectContributionEvidence.id = netEffectContributionFoi;
                        dispatch(resourceUpdated({
                            id: netEffectContributionFoi,
                            displayTitle: netEffectContributionEvidence.title,
                            resourceType: "Evidence",
                            resourceJson: combinedDictionary[netEffectContributionFoi],
                            revisedResourceJson: netEffectContributionEvidence,
                            usageData: {
                                editpermission: true,
                                readpermission: true,
                                status: "active"
                            }
                        }));
                    } else {
                        let resourceFOI;
                        let response = await submitFhirJson(netEffectContributionEvidence, netEffectContributionEvidence.title, apiToken);
                        if (response?.success) {
                            resourceFOI = response.formstateid;
                            netEffectContributionEvidence.id = resourceFOI;
                            let resourceBody = {
                                id: resourceFOI,
                                displayTitle: netEffectContributionEvidence.title,
                                resourceType: "Evidence",
                                resourceJson: netEffectContributionEvidence,
                                revisedResourceJson: netEffectContributionEvidence,
                                usageData: {
                                    totalviews: 0,
                                    totaledits: 1,
                                    totalusersviewed: 0,
                                    totalusersedited: 1,
                                    editpermission: true,
                                    readpermission: true,
                                    adminpermission: true,
                                    editors: [{ userid: userId, name: userName }],
                                    status: "active"
                                }
                            }
                            dispatch(resourceAdded(resourceBody));
                        }
                    }
                }
            }
            if (!isNaN(netEffectContributionLow) && !isNaN(netEffectContributionHigh)) {
                let confidenceIntervalWidth = (netEffectContributionHigh - netEffectContributionLow) / 100;
                let standardDeviation = confidenceIntervalWidth / 3.92;
                let standardDeviationSquared = standardDeviation * standardDeviation;
                netEffectStandardDeviationSquared += standardDeviationSquared;
            }
        }
    }
    findingsSection.sort((a, b) => {
        let aValue = 999999999;
        let bValue = 999999999;
        if (a.section && a.section.length >= 7 && a.section[6].text && a.section[6].text.div) {
            let aStarterValue = a.section[6].text.div;
            aValue = parseFloat(aStarterValue.split(' (')[0]);
        }
        if (b.section && b.section.length >= 7 && b.section[6].text && b.section[6].text.div) {
            let bStarterValue = b.section[6].text.div;
            bValue = parseFloat(bStarterValue.split(' (')[0]);
        }
        if (aValue < bValue) {
            return 1;
        }
        if (aValue > bValue) {
            return -1;
        }
        return 0;
    })

    let netEffectPointEstimateSpan = valueSpanWithStyle(netEffectPointEstimate, 2, true);
    let netEffectDisplay = netEffectPointEstimateSpan;
    let netEffectLowerBound;
    let netEffectUpperBound;
    if (netEffectStandardDeviationSquared) {
        let netEffectStandardDeviation = Math.sqrt(netEffectStandardDeviationSquared);
        netEffectLowerBound = netEffectPointEstimate - (1.96 * netEffectStandardDeviation);
        let netEffectLowerBoundSpan = valueSpanWithStyle(netEffectLowerBound, 2, false);
        netEffectUpperBound = netEffectPointEstimate + (1.96 * netEffectStandardDeviation);
        let netEffectUpperBoundSpan = valueSpanWithStyle(netEffectUpperBound, 2, false);
        netEffectDisplay += " (" + netEffectLowerBoundSpan + " to " + netEffectUpperBoundSpan + ")";
    }
    let netEffectEstimateSection = {
        'title': 'Net Effect Estimate',
        'code': {
            "coding": [
                {
                    "system": "https://fevir.net/resources/CodeSystem/179423",
                    "code": "net-effect-estimate",
                    "display": "Net Effect Estimate"
                }
            ],
            'text': 'Net Effect Estimate'
        },
        'text': { 'status': 'generated', 'div': "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectDisplay + "</p></div>" }
    };

    if (netEffectDisplay === "<span><b>0</b></span>") {
        netEffectEstimateSection.text = {
            'status': 'empty',
            'div': "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Not yet calculated.</p></div>"
        };
    }
    if (netEffectPointEstimate && !isNaN(netEffectPointEstimate)) {
        //create or replace the NetEffectEstimate Evidence Resource
        let netEffectEstimateEvidence = {
            "resourceType": "Evidence",
            "id": "",
            "meta": {
                "profile": [
                    "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-estimate"
                ]
            },
            "title": "NetEffectEstimate for " + combinedDictionary?.[compositionId]?.title || userName,
            "status": "active",
            "author": [
                {
                    "name": "Computable Publishing: Net Effect Generator"
                },
                {
                    "name": userName
                }
            ],
            "publisher": "Computable Publishing LLC",
            "contact": [
                {
                    "telecom": [
                        {
                            "system": "email",
                            "value": "support@computablepublishing.com"
                        }
                    ]
                }
            ],
            "copyright": "https://creativecommons.org/licenses/by-nc-sa/4.0/",
            "variableDefinition": [
                {
                    "description": "population definition not provided",
                    "variableRole": "population"
                },
                {
                    "description": "intervention vs. comparator",
                    "variableRole": "exposure",
                    "comparatorCategory": "comparator"
                },
                {
                    "description": "composite outcome derived from NetEffectContributions",
                    "variableRole": "outcome"
                }
            ],
            "statistic": [
                {
                    "statisticType": {
                        "coding": [
                            {
                                "system": "https://fevir.net/sevco",
                                "code": "STATO:0000424",
                                "display": "risk difference"
                            }
                        ]
                    },
                    "quantity": {
                        "value": netEffectPointEstimate
                    },
                    "attributeEstimate": [
                        {
                            "type": {
                                "coding": [
                                    {
                                        "system": "https://fevir.net/sevco",
                                        "code": "STATO:0000196",
                                        "display": "confidence interval"
                                    }
                                ]
                            },
                            "level": 0.95,
                            "range": {
                                "low": {
                                    "value": netEffectLowerBound
                                },
                                "high": {
                                    "value": netEffectUpperBound
                                }
                            }
                        }
                    ],
                    "modelCharacteristic": [
                        {
                            "code": {
                                "text": "Net effect analysis"
                            }
                        }
                    ]
                }
            ]
        };
        let netEffectFoi;
        if (priorNetEffectEstimateSection?.entry?.[0]?.reference) {
            netEffectFoi = getIdFromReference(priorNetEffectEstimateSection.entry[0]);
        }
        if (netEffectFoi && combinedDictionary[netEffectFoi]) {
            netEffectEstimateEvidence.id = netEffectFoi;
            netEffectEstimateSection.entry = [
                {
                    "reference": "Evidence/" + netEffectFoi,
                    "type": "Evidence",
                    "display": netEffectEstimateEvidence.title
                }
            ];
            if (JSON.stringify(combinedDictionary[netEffectFoi].statistic) !== JSON.stringify(netEffectEstimateEvidence.statistic)) {
                dispatch(resourceUpdated({
                    id: netEffectFoi,
                    displayTitle: netEffectEstimateEvidence.title,
                    resourceType: "Evidence",
                    resourceJson: combinedDictionary[netEffectFoi],
                    revisedResourceJson: netEffectEstimateEvidence,
                    usageData: {
                        editpermission: true,
                        readpermission: true,
                        status: "active"
                    }
                }));
                updateChangeReport(dispatch, resourceChanges, netEffectFoi,
                    "ArrayOfStatistic", "Evidence.statistic", combinedDictionary[netEffectFoi].statistic,
                    netEffectEstimateEvidence.statistic);
            }
        } else {
            let resourceFOI;
            let response = await submitFhirJson(netEffectEstimateEvidence, netEffectEstimateEvidence.title, apiToken);
            if (response?.success) {
                resourceFOI = response.formstateid;
                netEffectEstimateEvidence.id = resourceFOI;
                netEffectEstimateSection.entry = [
                    {
                        "reference": "Evidence/" + resourceFOI,
                        "type": "Evidence",
                        "display": netEffectEstimateEvidence.title
                    }
                ]
                let resourceBody = {
                    id: resourceFOI,
                    displayTitle: netEffectEstimateEvidence.title,
                    resourceType: "Evidence",
                    resourceJson: netEffectEstimateEvidence,
                    revisedResourceJson: netEffectEstimateEvidence,
                    usageData: {
                        totalviews: 0,
                        totaledits: 1,
                        totalusersviewed: 0,
                        totalusersedited: 1,
                        editpermission: true,
                        readpermission: true,
                        adminpermission: true,
                        editors: [{ userid: userId, name: userName }],
                        status: "active"
                    }
                }
                dispatch(resourceAdded(resourceBody));
                updateChangeReport(dispatch, resourceChanges, resourceFOI,
                    "Resource", "Evidence", null,
                    netEffectEstimateEvidence);
            }
        }
    }
    return netEffectEstimateSection;
}

const reloadFindingsSectionAndNetEffectEstimate = async (startingNetEffectContributionsState, netEffectContributions,
    netEffectEstimateSectionState, ratingDictionary,
    combinedDictionary, dispatch, userId, userName, compositionId, apiToken,
    setNetEffectEstimateSectionState, baselineRiskResources, setFindingsSectionChangedState, resourceChanges) => {
    let findingsSection = JSON.parse(JSON.stringify(netEffectContributions));
    let netEffectSection = JSON.parse(JSON.stringify(netEffectEstimateSectionState));
    let startingFindingsSection = JSON.parse(JSON.stringify(startingNetEffectContributionsState));
    let startingNetEffectSection = JSON.parse(JSON.stringify(netEffectEstimateSectionState));
    let netEffectEstimateSection = await recalculateFindingsSectionFromReferences(findingsSection,
        ratingDictionary, combinedDictionary, dispatch, userId, userName, compositionId, apiToken,
        netEffectSection, baselineRiskResources, resourceChanges);
    if (findingsSection && JSON.stringify(startingFindingsSection) !== JSON.stringify(findingsSection)) {
        console.log("findingsSection is different so will be reloaded")
        dispatch(loadNetEffectContributions({ netEffectContributions: findingsSection }));
        setFindingsSectionChangedState(true);
    }
    if (JSON.stringify(startingNetEffectSection) !== JSON.stringify(netEffectEstimateSection)) {
        setNetEffectEstimateSectionState(netEffectEstimateSection);
    }
}

const recalculateNetEffectContributionFromEvidenceChanges = async (netEffectContributionSection,
    ratingDictionary, combinedDictionary, controlEvidenceResourceState, comparativeEvidenceResourceState,
    itemIndex, dispatch, setCellDisplayState, apiToken, userId, userName, compositionId,
    evidenceReferencesState
) => {
    console.log("recalculateNetEffectContributionFromEvidenceChanges")
    let initialNetEffectContributionSection = JSON.parse(JSON.stringify(netEffectContributionSection));
    let revisedNetEffectContributionSection = JSON.parse(JSON.stringify(netEffectContributionSection));
    let targetFoi = getIdFromReference(revisedNetEffectContributionSection.focus);
    let relativeImportanceMultiplier = 0;
    let desirability = "undesirable";
    let importanceReference;
    if (ratingDictionary[targetFoi]?.percentageValue) {
        revisedNetEffectContributionSection.section[1].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (ratingDictionary[targetFoi].desirabilityDisplay || "Presumed Undesirable") + "</p></div>"
        };
        if (ratingDictionary[targetFoi].importanceReference) {
            revisedNetEffectContributionSection.section[1].entry = [ratingDictionary[targetFoi].importanceReference];
            importanceReference = ratingDictionary[targetFoi].importanceReference;
        }
        if (ratingDictionary[targetFoi].desirabilityDisplay === "Desirable") {
            desirability = 1;
        }
        revisedNetEffectContributionSection.section[2].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + ratingDictionary[targetFoi].percentageValue + "%</p></div>"
        };
        if (ratingDictionary[targetFoi].importanceReference) {
            revisedNetEffectContributionSection.section[2].entry = [ratingDictionary[targetFoi].importanceReference];
        }
        if (ratingDictionary[targetFoi].percentageValue) {
            relativeImportanceMultiplier = ratingDictionary[targetFoi].percentageValue;
        }
    }
    if (!relativeImportanceMultiplier && revisedNetEffectContributionSection.section[2]?.entry?.[0]) {
        importanceReference = revisedNetEffectContributionSection.section[2].entry[0];
        let outcomeRatingFoi = getIdFromReference(revisedNetEffectContributionSection.section[2].entry[0]);
        let outcomeImportance = combinedDictionary[outcomeRatingFoi];
        if (outcomeImportance?.content?.length > 0) {
            for (const content of outcomeImportance.content) {
                if (content.type?.coding?.[0]?.code === "outcome-desirability") {
                    revisedNetEffectContributionSection.section[1].text = {
                        "status": "generated",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + (content.classifier?.[0]?.coding?.[0]?.display || "Presumed Undesirable") + "</p></div>"
                    };
                    if (content.classifier?.[0]?.coding?.[0]?.display === "Desirable") {
                        desirability = 1;
                    }
                } else if (content.type?.coding?.[0]?.code === "relative-importance") {
                    revisedNetEffectContributionSection.section[2].text = {
                        "status": "generated",
                        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + content.quantity?.value + "%</p></div>"
                    };
                    if (content.quantity?.value) {
                        relativeImportanceMultiplier = content.quantity.value;
                    }
                }
            }
        }
    }
    let controlEventRate = NaN;
    let controlEventRateDisplay = "[Not a proportion.]";
    let riskDifference = NaN;
    let riskDifferenceDisplay = '[Not a difference in proportions.]';
    let interventionEventRate = NaN;
    let interventionEventRateDisplay = '[Not a proportion.]';
    let riskDifferenceLowValue = NaN;
    let riskDifferenceLowValueDisplay = 'No lower bound';
    let riskDifferenceHighValue = NaN;
    let riskDifferenceHighValueDisplay = 'No upper bound';
    let controlEvidence;
    let comparativeEvidence;
    let cellDisplay = "Edit Resource Content to add initial statistic.";
    if (controlEvidenceResourceState) {
        controlEvidence = controlEvidenceResourceState
    }
    if (comparativeEvidenceResourceState) {
        comparativeEvidence = comparativeEvidenceResourceState;
    }
    [controlEventRate, controlEventRateDisplay] = extractControlEventRate(controlEvidence, controlEventRate, controlEventRateDisplay);
    if (itemIndex === 3 && controlEvidence) {
        cellDisplay = controlEventRateDisplay;
    }

    [riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
        riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
        riskDifferenceHighValueDisplay] = extractRiskDifferenceAndInterventionEventRate(controlEventRate, riskDifference, riskDifferenceDisplay, interventionEventRate, interventionEventRateDisplay,
            riskDifferenceLowValue, riskDifferenceLowValueDisplay, riskDifferenceHighValue,
            riskDifferenceHighValueDisplay, comparativeEvidence, dispatch, compositionId);

    if (itemIndex === 4 && controlEvidence && comparativeEvidence) {
        cellDisplay = interventionEventRateDisplay
    };
    if (itemIndex === 5 && comparativeEvidence) {
        cellDisplay = riskDifferenceDisplay;
    }
    if (cellDisplay) {
        setCellDisplayState(cellDisplay);
    }
    revisedNetEffectContributionSection.section[3].text = {
        "status": "generated",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + controlEventRateDisplay + "</p></div>"
    };
    revisedNetEffectContributionSection.section[3].entry = [evidenceReferencesState.controlEvidenceReference];
    revisedNetEffectContributionSection.section[5].text = {
        "status": "generated",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + riskDifferenceDisplay + "</p></div>"
    };
    revisedNetEffectContributionSection.section[5].entry = [evidenceReferencesState.comparativeEvidenceReference];
    revisedNetEffectContributionSection.section[4].text = {
        "status": "generated",
        "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + interventionEventRateDisplay + "</p></div>"
    };
    revisedNetEffectContributionSection.section[4].entry = [
        evidenceReferencesState.controlEvidenceReference,
        evidenceReferencesState.comparativeEvidenceReference
    ];
    let [netEffectContribution, netEffectContributionLow,
        netEffectContributionHigh] = calculateNetEffectContributionWithConfidence(riskDifference,
            relativeImportanceMultiplier, desirability, riskDifferenceLowValue, riskDifferenceHighValue);
    let netEffectContributionDisplay = createNetEffectContributionDisplay(netEffectContribution, netEffectContributionLow, netEffectContributionHigh);

    if (!revisedNetEffectContributionSection.section[6].text ||
        revisedNetEffectContributionSection.section[6].text.div !== "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
    ) {
        revisedNetEffectContributionSection.section[6].text = {
            "status": "generated",
            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>" + netEffectContributionDisplay + "</p></div>"
        };
        if (netEffectContributionDisplay !== "[Not calculable.]") {
            //create or replace the NetEffectContribution Evidence Resource
            let netEffectContributionEvidence = {
                "resourceType": "Evidence",
                "id": "",
                "meta": {
                    "profile": [
                        "http://hl7.org/fhir/uv/ebm/StructureDefinition/net-effect-contribution"
                    ]
                },
                "contained": [comparativeEvidence.contained],
                "title": "NetEffectContribution for " + comparativeEvidence.title + " for " + userName,
                "status": "active",
                "author": [
                    {
                        "name": "Computable Publishing: Net Effect Generator"
                    },
                    {
                        "name": userName
                    }
                ],
                "publisher": "Computable Publishing LLC",
                "contact": [
                    {
                        "telecom": [
                            {
                                "system": "email",
                                "value": "support@computablepublishing.com"
                            }
                        ]
                    }
                ],
                "copyright": "https://creativecommons.org/licenses/by-nc-sa/4.0/",
                "variableDefinition": [
                    comparativeEvidence.variableDefinition?.[0] ||
                    {
                        "description": "population definition missing in comparative evidence",
                        "variableRole": "population"
                    },
                    comparativeEvidence.variableDefinition?.[1] ||
                    {
                        "description": "exposure definition missing in comparative evidence",
                        "variableRole": "exposure"
                    },
                    comparativeEvidence.variableDefinition?.[2] ||
                    {
                        "description": "outcome definition missing in comparative evidence",
                        "variableRole": "outcome"
                    },
                    {
                        "extension": [
                            {
                                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/relative-outcome-importance",
                                "valueReference": importanceReference
                            }
                        ],
                        "variableRole": "covariate",
                        "roleSubtype": {
                            "text": "Relative Value Multiplier"
                        },
                        "observed": {
                            "display": "Relative Importance Multiplier"
                        }
                    }
                ],
                "statistic": [
                    {
                        "statisticType": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/sevco",
                                    "code": "STATO:0000424",
                                    "display": "risk difference"
                                }
                            ]
                        },
                        "quantity": {
                            "value": netEffectContribution
                        },
                        "attributeEstimate": [
                            {
                                "type": {
                                    "coding": [
                                        {
                                            "system": "https://fevir.net/sevco",
                                            "code": "STATO:0000196",
                                            "display": "confidence interval"
                                        }
                                    ]
                                },
                                "level": 0.95,
                                "range": {
                                    "low": {
                                        "value": netEffectContributionLow
                                    },
                                    "high": {
                                        "value": netEffectContributionHigh
                                    }
                                }
                            }
                        ],
                        "modelCharacteristic": [
                            {
                                "code": {
                                    "text": "Net effect contribution analysis"
                                }
                            }
                        ]
                    }
                ]
            };
            if (!comparativeEvidence.contained) {
                delete netEffectContributionEvidence.contained;
            }
            let netEffectContributionFoi;
            if (revisedNetEffectContributionSection.section[6].entry?.[0]?.reference) {
                netEffectContributionFoi = getIdFromReference(revisedNetEffectContributionSection.section[6].entry[0]);
            }
            if (combinedDictionary[netEffectContributionFoi]) {
                netEffectContributionEvidence.id = netEffectContributionFoi;
                dispatch(resourceUpdated({
                    id: netEffectContributionFoi,
                    displayTitle: netEffectContributionEvidence.title,
                    resourceType: "Evidence",
                    resourceJson: combinedDictionary[netEffectContributionFoi],
                    revisedResourceJson: netEffectContributionEvidence,
                    usageData: {
                        editpermission: true,
                        readpermission: true,
                        status: "active"
                    }
                }));
            } else {
                let resourceFOI;
                let response = await submitFhirJson(netEffectContributionEvidence, netEffectContributionEvidence.title, apiToken);
                if (response?.success) {
                    resourceFOI = response.formstateid;
                    netEffectContributionEvidence.id = resourceFOI;
                    let resourceBody = {
                        id: resourceFOI,
                        displayTitle: netEffectContributionEvidence.title,
                        resourceType: "Evidence",
                        resourceJson: netEffectContributionEvidence,
                        revisedResourceJson: netEffectContributionEvidence,
                        usageData: {
                            totalviews: 0,
                            totaledits: 1,
                            totalusersviewed: 0,
                            totalusersedited: 1,
                            editpermission: true,
                            readpermission: true,
                            adminpermission: true,
                            editors: [{ userid: userId, name: userName }],
                            status: "active"
                        }
                    }
                    dispatch(resourceAdded(resourceBody));
                }
            }
        }
    }
    if (JSON.stringify(initialNetEffectContributionSection) !==
        JSON.stringify(revisedNetEffectContributionSection)) {
        revisedNetEffectContributionSection.itemChanged = true;
        dispatch(netEffectContributionUpdated(revisedNetEffectContributionSection));
    }
};

const SoNEEditTableCellModal = ({ targetFoi, item, itemIndex, setOpenEditModalState,
    setTriggerReloadState, findingsSectionIndex }) => {

    const dispatch = useDispatch();
    const comparativeEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const combinedDictionary = comparativeEvidenceMap.combinedDictionary;
    const compositionId = comparativeEvidenceMap.compositionId;
    const netEffectContributionSection = useSelector(state => selectNetEffectContributionByTargetId(state, targetFoi));
    const summaryOfFindingsSectionFromSlice = useSelector(state => selectTopSectionById(state, findingsSectionIndex));
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const ratingResources = useSelector(state => selectAllImportance(state));
    const ratingDictionary = {};
    for (let key in ratingResources) {
        ratingDictionary[ratingResources[key].id] = ratingResources[key];
    }
    const baselineRiskEntry = useSelector(state => selectBaselineRiskById(state, targetFoi));
    const targetOutcome = useSelector(state => selectOutcomeVariableById(state, targetFoi));
    const netEffectContributions = useSelector(selectAllNetEffectContributions);
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));

    const [evidenceReferencesState, setEvidenceReferencesState] = useState({});
    const [cellDisplayState, setCellDisplayState] = useState({});
    const [controlEvidenceResourceState, setControlEvidenceResourceState] = useState(null);
    const [initialControlEvidenceResourceState, setInitialControlEvidenceResourceState] = useState(null);
    const [comparativeEvidenceResourceState, setComparativeEvidenceResourceState] = useState(null);
    const [initialComparativeEvidenceResourceState, setInitialComparativeEvidenceResourceState] = useState(null);
    const [closeModalState, setCloseModalState] = useState(false);

    useEffect(() => {
        let startingEvidenceReferencesState = {
            "controlEvidenceReference": "", "comparativeEvidenceReference": ""
        };
        if (item) {
            if (item.text) {
                setCellDisplayState(clearDivWrapper(item.text.div));
            } else {
                setCellDisplayState("[No data.]");
            }
            if (item.entry?.length > 0) {
                let baselineRiskFoi;
                let comparativeEvidenceFoi;
                let baselineRiskReference;
                let comparativeEvidenceReference;
                if (itemIndex === 3) {
                    baselineRiskFoi = getIdFromReference(item.entry[0]);
                    baselineRiskReference = item.entry[0];
                } else if (itemIndex === 5) {
                    comparativeEvidenceFoi = getIdFromReference(item.entry[0]);
                    comparativeEvidenceReference = item.entry[0];
                } else if (itemIndex === 4) {
                    baselineRiskFoi = getIdFromReference(item.entry[0]);
                    baselineRiskReference = item.entry[0];
                    if (item.entry[1]) {
                        comparativeEvidenceFoi = getIdFromReference(item.entry[1]);
                        comparativeEvidenceReference = item.entry[1];
                    }
                }
                if (!baselineRiskFoi && netEffectContributionSection.section[3].entry?.[0]) {
                    baselineRiskFoi = getIdFromReference(netEffectContributionSection.section[3].entry[0]);
                    baselineRiskReference = netEffectContributionSection.section[3].entry[0];
                }
                if (!comparativeEvidenceFoi && netEffectContributionSection.section[5].entry?.[0]) {
                    comparativeEvidenceFoi = getIdFromReference(netEffectContributionSection.section[5].entry[0]);
                    comparativeEvidenceReference = netEffectContributionSection.section[5].entry[0];
                }
                if (baselineRiskReference) {
                    startingEvidenceReferencesState.controlEvidenceReference = baselineRiskReference;
                }
                if (comparativeEvidenceReference) {
                    startingEvidenceReferencesState.comparativeEvidenceReference = comparativeEvidenceReference;
                }
                if (baselineRiskFoi && combinedDictionary[baselineRiskFoi]) {
                    setControlEvidenceResourceState(combinedDictionary[baselineRiskFoi]);
                    setInitialControlEvidenceResourceState(combinedDictionary[baselineRiskFoi]);
                } else if (baselineRiskFoi) {
                    setControlEvidenceResourceState({
                        "resourceType": "Evidence",
                        "id": baselineRiskFoi,
                        "statistic": []
                    });
                    setInitialControlEvidenceResourceState({
                        "resourceType": "Evidence",
                        "id": baselineRiskFoi,
                        "statistic": []
                    });
                }
                if (comparativeEvidenceFoi && combinedDictionary[comparativeEvidenceFoi]) {
                    setComparativeEvidenceResourceState(combinedDictionary[comparativeEvidenceFoi]);
                    setInitialComparativeEvidenceResourceState(combinedDictionary[comparativeEvidenceFoi]);
                } else if (comparativeEvidenceFoi) {
                    setComparativeEvidenceResourceState({
                        "resourceType": "Evidence",
                        "id": comparativeEvidenceFoi,
                        "statistic": []
                    });
                    setInitialComparativeEvidenceResourceState({
                        "resourceType": "Evidence",
                        "id": comparativeEvidenceFoi,
                        "statistic": []
                    });
                }
            }
        }
        setEvidenceReferencesState(startingEvidenceReferencesState);
    }, []);

    useEffect(() => {
        if (evidenceReferencesState.controlEvidenceReference) {
            let baselineRiskFoi = getIdFromReference(evidenceReferencesState.controlEvidenceReference);
            if (baselineRiskFoi && combinedDictionary[baselineRiskFoi]) {
                setControlEvidenceResourceState(combinedDictionary[baselineRiskFoi]);
            } else {
                setControlEvidenceResourceState({
                    "resourceType": "Evidence",
                    "id": baselineRiskFoi,
                    "statistic": []
                });
            }
        }
        if (evidenceReferencesState.comparativeEvidenceReference) {
            let comparativeEvidenceFoi = getIdFromReference(evidenceReferencesState.comparativeEvidenceReference);
            if (comparativeEvidenceFoi && combinedDictionary[comparativeEvidenceFoi]) {
                setComparativeEvidenceResourceState(combinedDictionary[comparativeEvidenceFoi]);
            } else {
                setComparativeEvidenceResourceState({
                    "resourceType": "Evidence",
                    "id": comparativeEvidenceFoi,
                    "statistic": []
                });
            }
        }
    }, [evidenceReferencesState.controlEvidenceReference, evidenceReferencesState.comparativeEvidenceReference]);

    useEffect(() => {
        if (controlEvidenceResourceState?.id &&
            JSON.stringify(controlEvidenceResourceState) !== JSON.stringify(initialControlEvidenceResourceState)) {
            let controlEventRate = NaN;
            let controlEventRateDisplay = "[Not a proportion.]";
            [controlEventRate, controlEventRateDisplay] = extractControlEventRate(controlEvidenceResourceState, controlEventRate, controlEventRateDisplay);
            let riskBody = {
                id: targetFoi,
                targetTitle: targetOutcome.title,
                baselineRiskFoi: controlEvidenceResourceState.id,
                fli: "",
                editPermission: true,
                controlEventRate: controlEventRate,
                controlEventRateDisplay: controlEventRateDisplay,
                resource: controlEvidenceResourceState,
                reference: evidenceReferencesState.controlEvidenceReference
            }
            if (!baselineRiskEntry || !baselineRiskEntry.id) {
                dispatch(baselineRiskAdded(riskBody));
            } else if (JSON.stringify(baselineRiskEntry.resource) !== JSON.stringify(controlEvidenceResourceState)) {
                dispatch(baselineRiskUpdated(riskBody));
            }
        }
        if ((controlEvidenceResourceState &&
            Object.keys(controlEvidenceResourceState).length > 0 &&
            JSON.stringify(controlEvidenceResourceState) !== JSON.stringify(initialControlEvidenceResourceState)
        )
            ||
            (comparativeEvidenceResourceState &&
                Object.keys(comparativeEvidenceResourceState).length > 0 &&
                JSON.stringify(comparativeEvidenceResourceState) !== JSON.stringify(initialComparativeEvidenceResourceState)
            )
        ) {
            recalculateNetEffectContributionFromEvidenceChanges(netEffectContributionSection,
                ratingDictionary, combinedDictionary, controlEvidenceResourceState, comparativeEvidenceResourceState,
                itemIndex, dispatch, setCellDisplayState, apiToken, userId, userName, compositionId,
                evidenceReferencesState);
        }
    }, [controlEvidenceResourceState, comparativeEvidenceResourceState]);

    useEffect(() => {
        if (closeModalState === true) {
            let findingsSection = netEffectContributions.map(section => {
                let newSection = JSON.parse(JSON.stringify(section));
                delete newSection.id;
                delete newSection.itemChanged;
                return newSection;
            });
            let newTopSectionDetail = JSON.parse(JSON.stringify(summaryOfFindingsSectionFromSlice));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.section || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.section || null);
            newTopSectionDetail.revisedSection.section = findingsSection;
            if (findingsSection &&
                previousFinalValue !== JSON.stringify(findingsSection)) {
                console.log("resourceUpdate for Composition.section[" + findingsSectionIndex + "].section");
                dispatch(topSectionUpdated(newTopSectionDetail));
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "ArrayOfSection",
                    "section[" + findingsSectionIndex + "].section",
                    previousInitialValue,
                    findingsSection);
            }
        }
    }, [closeModalState]);

    let modalContent = <div style={{
        paddingTop: "6px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        {(cellDisplayState && evidenceReferencesState &&
            Object.keys(evidenceReferencesState).length > 0) &&
            <>
                <Button style={{ padding: "6px", position: "absolute", right: "14px" }} className="formButton negative"
                    content="✖"
                    onClick={() => {
                        setCloseModalState(true);
                        setOpenEditModalState(prevState => { return { ...prevState, openModal: false }; });
                        setTriggerReloadState(true);
                    }} />
                <h3>Edit Table Cell Content</h3>
                <br />
                <DisplayFromFHIRXhtml xhtml={cellDisplayState} />
                <br /><br />
                {(itemIndex === 3 || itemIndex === 4) &&
                    <>
                        <h3>Change Resource Reference for the Control Only Evidence (Baseline Risk)</h3>
                        <br />
                        <ReferenceEntry elementName='controlEvidenceReference'
                            fieldLabel='ComparatorOnlyEvidence Reference' enableCreation={true}
                            startingResourceType={"Evidence"} referencedResourceTypes={["Evidence"]}
                            setProfile={"ComparatorOnlyEvidence"}
                            startingValue={evidenceReferencesState.controlEvidenceReference}
                            setReferencedResourceState={setControlEvidenceResourceState}
                            setResourceState={setEvidenceReferencesState} />
                        <br /><br />
                    </>}
                {(itemIndex === 5 || itemIndex === 4) &&
                    <>
                        <h3>Change Resource Reference for the Comparative Evidence</h3>
                        <br />
                        <ReferenceEntry elementName='comparativeEvidenceReference'
                            fieldLabel='ComparativeEvidence Reference' enableCreation={true}
                            startingResourceType={"Evidence"} referencedResourceTypes={["Evidence"]}
                            setProfile={"ComparativeEvidence"}
                            startingValue={evidenceReferencesState.comparativeEvidenceReference}
                            setReferencedResourceState={setComparativeEvidenceResourceState}
                            setResourceState={setEvidenceReferencesState} />
                        <br /><br />
                    </>}
                <Button style={{ color: "#FFFFFF", width: "230px", float: "left" }} className="formButton"
                    content="Close Modal" positive
                    onClick={() => {
                        setCloseModalState(true);
                        setOpenEditModalState(prevState => { return { ...prevState, openModal: false }; });
                        setTriggerReloadState(true);
                    }}
                />
                <br />
            </>}
    </div>;

    return (
        <Modal
            style={{ padding: "0px", margin: "0px" }}
            dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
            open={true}
            centered={false}
            content={modalContent}
        />
    )
};

const colorSchemeSwitch = (div, colorScheme) => {
    if (colorScheme === "red-green") {
        return div;
    } else if (colorScheme === "orange-blue") {
        return div.replaceAll('style="color: #AA0000"', 'style="color: #AA6000FF"').replaceAll('style="color: #00AA00"', 'style="color: #0033AAFF"');
    } else if (colorScheme === "greyscale") {
        return div.replaceAll('style="color: #AA0000"', 'style="color: #999999"').replaceAll('style="color: #00AA00"', 'style="color: #555555"');
    }
    return
}

const SummaryOfNetEffectTable = ({ findingsSectionIndex, netEffectEstimateSectionIndex,
    subject }) => {
    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const editMode = useSelector(state => selectEditMode(state));
    const colorScheme = useSelector(selectColorScheme);
    const userId = useSelector(selectCurrentUserId);
    const userName = useSelector(selectCurrentUserName);
    const resourceCollection = useSelector(state => selectAllResources(state));
    const ratingStatus = useSelector(selectOutcomeImportanceStatus);
    const ratingResources = useSelector(state => selectAllImportance(state));
    const ratingDictionary = {};
    for (let key in ratingResources) {
        ratingDictionary[ratingResources[key].id] = ratingResources[key];
    }
    const baselineRiskStatus = useSelector(selectBaselineRiskStatus);
    const baselineRiskResources = useSelector(state => selectAllBaselineRisks(state));

    const outcomeVariablesStatus = useSelector(selectOutcomeVariablesStatus);
    const outcomeVariables = useSelector(state => selectAllOutcomeVariables(state));

    const comparativeEvidenceMap = useSelector(selectComparativeEvidenceMap);
    const compositionId = comparativeEvidenceMap.compositionId;
    const netEffectContributions = useSelector(state => selectAllNetEffectContributions(state));
    const netEffectContributionsStatus = useSelector(selectNetEffectContributionsStatus);
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const summaryOfFindingsSectionFromSlice = useSelector(state => selectTopSectionById(state, findingsSectionIndex));
    const netEffectEstimateSectionFromSlice = useSelector(state => selectTopSectionById(state, netEffectEstimateSectionIndex));

    const allTopSections = useSelector(selectAllTopSections);
    const topSectionsStatus = useSelector(selectTopSectionsStatus);
    const sections = allTopSections.map(entity => { return entity.revisedSection });

    const [openEditModalState, setOpenEditModalState] = useState({ "openModal": false });
    const [openAddTableRowModalState, setOpenAddTableRowModalState] = useState({ "openModal": false });
    const [netEffectEstimateSectionState, setNetEffectEstimateSectionState] = useState({});
    const [startingNetEffectEstimateSectionState, setStartingNetEffectEstimateSectionState] = useState([]);
    const [triggerReloadState, setTriggerReloadState] = useState(false);
    const [findingsSectionChangedState, setFindingsSectionChangedState] = useState(false);
    const [remountTable, setRemountTable] = useState(0);
    const [startingNetEffectContributionsState, setStartingNetEffectContributionsState] = useState([]);

    useEffect(() => {
        if (topSectionsStatus === "succeeded" && sections.length > 0 && findingsSectionIndex !== null) {
            if (netEffectContributionsStatus === "idle") {
                let findingsSection;
                let netEffectSection;
                if (sections[findingsSectionIndex] &&
                    sections[findingsSectionIndex].code?.coding?.[0]?.code === "summary-of-findings" &&
                    sections[findingsSectionIndex].section.length > 0) {
                    findingsSection = JSON.parse(JSON.stringify(sections[findingsSectionIndex].section));
                    let netEffectContributionsArray = findingsSection.map(section => {
                        let targetFoi = getIdFromReference(section.focus);
                        section.id = targetFoi;
                        section.itemChanged = false;
                        return section;
                    });
                    let netEffectContributionsBody = { netEffectContributions: netEffectContributionsArray };
                    console.log("about to dispatch")
                    dispatch(loadNetEffectContributions(netEffectContributionsBody));
                }
                if (sections[netEffectEstimateSectionIndex] &&
                    sections[netEffectEstimateSectionIndex]?.code?.coding?.[0]?.code === "net-effect-estimate") {
                    netEffectSection = JSON.parse(JSON.stringify(sections[netEffectEstimateSectionIndex]));
                    setNetEffectEstimateSectionState(netEffectSection);
                    setStartingNetEffectEstimateSectionState(netEffectSection);
                }
                if (findingsSection && !netEffectSection) {
                    netEffectSection = {
                        "title": "Net Effect Estimate",
                        "code": {
                            "coding": [
                                {
                                    "system": "https://fevir.net/resources/CodeSystem/179423",
                                    "code": "net-effect-estimate",
                                    "display": "Net Effect Estimate"
                                }
                            ],
                            "text": "Net Effect Estimate"
                        },
                        "text": {
                            "status": "generated",
                            "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\"><p>Not yet calculated.</p></div>"
                        }
                    }
                    setNetEffectEstimateSectionState(netEffectSection);
                    setStartingNetEffectEstimateSectionState(netEffectSection);
                }
            }
            if (netEffectContributionsStatus === "succeeded") {
                setStartingNetEffectContributionsState(JSON.parse(JSON.stringify(netEffectContributions)));
            }
        }
    }, [topSectionsStatus, netEffectContributionsStatus, findingsSectionIndex]);

    useEffect(() => {
        if (netEffectContributionsStatus === "idle") {
            //
        } else if (editMode === true) {
            if (ratingStatus === "idle" || baselineRiskStatus === "idle" || outcomeVariablesStatus === "idle") {
                loadParameterResources(dispatch, netEffectContributions,
                    apiToken, comparativeEvidenceMap.combinedDictionary, resourceCollection,
                    subject);
            }
            if (ratingStatus === "succeeded" && outcomeVariablesStatus === "succeeded" &&
                netEffectContributions && ratingDictionary && resourceCollection?.length > 0 &&
                Object.keys(ratingDictionary).length > 0 &&
                Object.keys(netEffectEstimateSectionState).length > 0) {
                let resourceDictionary = JSON.parse(JSON.stringify(comparativeEvidenceMap.combinedDictionary));
                for (const entry of resourceCollection) {
                    resourceDictionary[entry.id] = entry.revisedResourceJson;
                }
                reloadFindingsSectionAndNetEffectEstimate(startingNetEffectContributionsState, netEffectContributions,
                    netEffectEstimateSectionState,
                    ratingDictionary, resourceDictionary, dispatch,
                    userId, userName, compositionId, apiToken,
                    setNetEffectEstimateSectionState, baselineRiskResources, setFindingsSectionChangedState,
                    resourceChanges);
            }
        }
    }, [ratingStatus, baselineRiskStatus, outcomeVariablesStatus, editMode, netEffectContributionsStatus]);

    useEffect(() => {
        if (triggerReloadState === true) {
            let resourceDictionary = JSON.parse(JSON.stringify(comparativeEvidenceMap.combinedDictionary));
            for (const entry of resourceCollection) {
                resourceDictionary[entry.id] = entry.revisedResourceJson;
            }
            reloadFindingsSectionAndNetEffectEstimate(startingNetEffectContributionsState, netEffectContributions,
                netEffectEstimateSectionState, ratingDictionary,
                resourceDictionary, dispatch, userId, userName,
                compositionId, apiToken,
                setNetEffectEstimateSectionState,
                baselineRiskResources, setFindingsSectionChangedState, resourceChanges);
            setTriggerReloadState(false);
            setRemountTable(prevState => prevState + 1);
        }
    }, [triggerReloadState]);

    useEffect(() => {
        if (findingsSectionChangedState === true) {
            let findingsSection = netEffectContributions.map(section => {
                let newSection = JSON.parse(JSON.stringify(section));
                delete newSection.id;
                delete newSection.itemChanged;
                return newSection;
            });
            let newTopSectionDetail = JSON.parse(JSON.stringify(summaryOfFindingsSectionFromSlice));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newTopSectionDetail.initialSection?.section || null;
            previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection?.section || null);
            newTopSectionDetail.revisedSection.section = findingsSection;
            if (findingsSection &&
                previousFinalValue !== JSON.stringify(findingsSection)) {
                console.log("resourceUpdate for Composition.section[" + findingsSectionIndex + "].section");
                dispatch(topSectionUpdated(newTopSectionDetail));
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "ArrayOfSection",
                    "section[" + findingsSectionIndex + "].section",
                    previousInitialValue,
                    findingsSection);
            }
        }
    }, [findingsSectionChangedState]);

    useEffect(() => {
        if (netEffectEstimateSectionState.text &&
            JSON.stringify(netEffectEstimateSectionState) !== JSON.stringify(startingNetEffectEstimateSectionState)) {
            if (netEffectEstimateSectionFromSlice) {
                let newTopSectionDetail = JSON.parse(JSON.stringify(netEffectEstimateSectionFromSlice));
                let previousInitialValue = null;
                let previousFinalValue = null;
                previousInitialValue = newTopSectionDetail.initialSection || null;
                previousFinalValue = JSON.stringify(newTopSectionDetail.revisedSection || null);

                newTopSectionDetail.revisedSection = netEffectEstimateSectionState;
                if (previousFinalValue !== JSON.stringify(netEffectEstimateSectionState)) {
                    console.log("resourceUpdate for Composition.section[" + netEffectEstimateSectionIndex + "]");
                    dispatch(topSectionUpdated(newTopSectionDetail));
                    updateChangeReport(dispatch, resourceChanges, compositionId,
                        "Section",
                        "section[" + netEffectEstimateSectionIndex + "]",
                        previousInitialValue,
                        netEffectEstimateSectionState);
                }
            } else {
                let sectionLength = allTopSections.length;
                let newTopSectionDetail = {
                    id: sectionLength,
                    displayTitle: "Net Effect Estimate",
                    compositionId: compositionId,
                    productFamily: "SummaryOfNetEffect",
                    initialSection: netEffectEstimateSectionState,
                    revisedSection: netEffectEstimateSectionState
                }
                dispatch(topSectionAdded(newTopSectionDetail));
                updateChangeReport(dispatch, resourceChanges, compositionId,
                    "ArrayOfSection",
                    "section[" + sectionLength + "].section",
                    null,
                    netEffectEstimateSectionState);
            }
        }
    }, [netEffectEstimateSectionState]);

    let netEffectPointEstimate = getNetEffectPointEstimate({ section: sections }) || null;

    let netEffectEstimate = getNetEffectEstimate({ section: sections }) || null;

    return <div>
        {netEffectContributionsStatus === "succeeded" &&
            <div>
                {editMode &&
                    <>
                        {(outcomeVariables && Object.keys(outcomeVariables).length > 0) ?
                            <>
                                <ReassignOutcomes
                                    setTriggerReloadState={setTriggerReloadState} />
                                <p></p>
                                <RateRelativeImportance subject={subject}
                                    setTriggerReloadState={setTriggerReloadState} />
                                <p></p>
                                <ChangeBaselineRiskEstimates
                                    setTriggerReloadState={setTriggerReloadState} />
                            </>
                            :
                            <>
                                <span onClick={() => {
                                    loadParameterResources(dispatch, netEffectContributions,
                                        apiToken, comparativeEvidenceMap.combinedDictionary, resourceCollection);
                                }}>Reload Source JSON</span>
                            </>
                        }
                        <br />
                        <br />
                        {openEditModalState.openModal &&
                            <SoNEEditTableCellModal
                                targetFoi={openEditModalState.targetFoi}
                                item={openEditModalState.item}
                                itemIndex={openEditModalState.itemIndex}
                                setOpenEditModalState={setOpenEditModalState}
                                setTriggerReloadState={setTriggerReloadState}
                                findingsSectionIndex={findingsSectionIndex} />}
                        {openAddTableRowModalState.openModal &&
                            <AddTableRowModal setOpenAddTableRowModalState={setOpenAddTableRowModalState}
                                setTriggerReloadState={setTriggerReloadState}
                            />}
                        {comparativeEvidenceMap?.combinedDictionary &&
                            <Button style={{ color: "#FFFFFF", width: "230px", float: "left" }}
                                className="formButton"
                                positive
                                content={"Recalculate Net Effect"}
                                onClick={() => {
                                    setTriggerReloadState(true);
                                }}
                            />
                        }
                    </>
                }
                <Table key={remountTable}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell key="outcome">
                                <p>Outcome</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="desirability">
                                <p>Desirability</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="importance">
                                <p>Relative Importance</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="control">
                                <p>Result Without Treatment</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="intervention">
                                <p>Result With Treatment (Calculated)</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="effect">
                                <p>Difference With Treatment (Confidence Interval)</p>
                            </Table.HeaderCell>
                            <Table.HeaderCell key="impact">
                                <p>Health Impact (Confidence Interval)</p>
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {netEffectContributions.map((row, rowIndex) => {
                            return <Table.Row key={rowIndex}>
                                {row.section.map((item, itemIndex) => {
                                    return <Table.Cell key={itemIndex}>
                                        <div>
                                            {(editMode && itemIndex > 2 && itemIndex < 6) &&
                                                <>
                                                    <span className={"unselectable"}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => {
                                                            setOpenEditModalState({
                                                                openModal: true,
                                                                targetFoi: getIdFromReference(row.focus),
                                                                item: item, itemIndex: itemIndex, entryIndex: rowIndex
                                                            });
                                                        }}>✎ Edit</span>
                                                    <br />
                                                    <br />
                                                </>}
                                            <span>
                                                <DisplayFromFHIRXhtml xhtml={colorSchemeSwitch(item.text.div, colorScheme)} />
                                            </span>
                                        </div>
                                    </Table.Cell>
                                })}
                            </Table.Row>
                        })}
                    </Table.Body>
                    <>{netEffectPointEstimate &&
                        <Table.Footer>
                            <Table.Row>
                                <Table.HeaderCell className="netEffectCell" textAlign='right'
                                    colSpan={7}>
                                    <b>Net Effect Point Estimate = <DisplayFromFHIRXhtml xhtml={netEffectPointEstimate} /></b>
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    }</>
                    <>{netEffectEstimate &&
                        <Table.Footer>
                            <Table.Row>
                                <Table.HeaderCell className="netEffectCell" textAlign='right'
                                    colSpan={7}>
                                    <b>Net Effect Estimate = <DisplayFromFHIRXhtml xhtml={colorSchemeSwitch(netEffectEstimate, colorScheme)} /></b>
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    }</>
                </Table>
                {editMode && <>
                    <Button style={{ color: "#FFFFFF", width: "230px", float: "left" }}
                        className="formButton"
                        content="+ Add Table Row" positive
                        onClick={() => {
                            setOpenAddTableRowModalState({ openModal: true });
                        }}
                    />
                    <br /><br />
                </>}
            </div>}
    </div>
}

export default SummaryOfNetEffectTable;
export { extractControlEventRate, extractRiskDifferenceAndInterventionEventRate };